import type {
  Pet,
  PetDetail,
  PetDetailForDisplay,
  PetDetailOption,
  PetDetailValueOption,
} from '@rover/react-lib/types/Pet';
import { kebabifyString } from '@rover/utilities/object-utilities';

// Returns the pet names in a comma separated list
export function petNameListify(petNames: string[]): string {
  return petNames.length > 0 ? petNames.join(', ') : '';
}

function getSpayedOrNeutered(pet: Pet): string {
  return pet.gender === 'm' ? 'Neutered' : 'Spayed';
}

type PetInformationKey = {
  key: string;
  display: string;
};

// Returns keys and display text for the pet details that are stored on the Pet object
export function getPetInformationKeys(pet: Pet): PetInformationKey[] {
  return [
    {
      key: 'houseTrained',
      display: 'House Trained',
    },
    {
      key: 'spayedNeutered',
      display: getSpayedOrNeutered(pet),
    },
    {
      key: 'playsChildren',
      display: 'Gets along with children',
    },
    {
      key: 'playsDogs',
      display: 'Gets along with dogs',
    },
    {
      key: 'playsCats',
      display: 'Gets along with cats',
    },
    {
      key: 'implant',
      display: 'Microchipped',
    },
  ];
}

// Returns a list of PetDetail objects for each property currently stored on the Pet object
// but not yet stored as a PetDetail
function getDerivedPetDetailsFromPetInformation(pet: Pet): PetDetailForDisplay[] {
  const { petDetails } = pet;

  if (!petDetails) {
    return [];
  }

  const newKeys = getPetInformationKeys(pet) // Only return properties where a corresponding PetDetail doesn't exist
    .filter((detail) => !petDetails.some((pd) => pd.slug === kebabifyString(detail.key))) // Only return properties where the value is not null
    .filter(
      (detail) => Object.prototype.hasOwnProperty.call(pet, detail.key) && pet[detail.key] !== null
    );
  return newKeys.map((detail) => {
    const value = pet[detail.key] ? 'yes' : 'no';
    return {
      slug: kebabifyString(detail.key),
      value,
      displayName: detail.display,
      valueDisplayName: `${value[0].toUpperCase()}${value.slice(1).toLowerCase()}`,
      additionalInformation: pet[`${detail.key}Desc`] || '',
    };
  });
}

// Returns a list of PetDetail objects from the property or derived from PetInformation
export function getPetDetailsFromPet(pet: Pet): PetDetailForDisplay[] {
  const { petDetails } = pet;
  const derivedPetDetails = getDerivedPetDetailsFromPetInformation(pet);
  return [...derivedPetDetails, ...(petDetails || [])];
}
export function createPetDetailFromOption(
  value: string,
  additionalInformation = '',
  petDetailOption: PetDetailOption
): PetDetail {
  const { slug, displayName, serviceTypes, multiselect } = petDetailOption;
  const { displayName: valueDisplayName } =
    petDetailOption.options.find((o: PetDetailValueOption) => o.value === value) || {};
  return {
    slug,
    value,
    additionalInformation,
    displayName,
    serviceTypes,
    multiselect,
    valueDisplayName: valueDisplayName || '',
  };
}

export function getPetOpkFromUrl(url: string): string {
  const petOpk = url.match(/pets\/([a-zA-z0-9]+)\//);
  return petOpk ? petOpk[1] : '';
}

const pets = {
  petNameListify,
  getPetInformationKeys,
  getPetDetailsFromPet,
  createPetDetailFromOption,
  getPetOpkFromUrl,
};

export default pets;



// WEBPACK FOOTER //
// ../react-lib/src/utils/pets.ts